/** @file Structural shape of each drawable kind (design 4.2, 17); references: validateDrawables. */
import type { Drawable } from '@zilo/pose3d-format/types/drawable';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkBoolean } from '@zilo/pose3d-format/validate/fields/checkBoolean';
import { checkEnum } from '@zilo/pose3d-format/validate/fields/checkEnum';
import { checkHinted } from '@zilo/pose3d-format/validate/fields/checkHinted';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { checkShape } from '@zilo/pose3d-format/validate/fields/checkShape';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { checkTuple } from '@zilo/pose3d-format/validate/fields/checkTuple';
import type { Shape } from '@zilo/pose3d-format/validate/fields/fieldCheck';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';
import { FACET_ENTRY_SHAPE } from '@zilo/pose3d-format/validate/shapes/facetEntryShape';

const name = checkString(NAME_PATTERN);
const common = { kind: checkString(), id: name, node: name, offset: checkTuple(3) };
const sheer = checkNumber({ min: 0, max: 2 });
const facets = checkArray(checkShape(FACET_ENTRY_SHAPE), 1);
const weight = (hint: string) => checkHinted(checkNumber({ min: 0, max: 1 }), hint);

/** `anchor.offset` (21.7): a vec3 in units, node space, along the node's own bone. */
const anchorOffset = checkHinted(checkTuple(3),
  "anchor.offset is [x, y, z] in units, node space: the second point along this node's own bone (21.7), e.g. [0, 0, -0.75]");

/** `Anchor` (17.4, 18.8); `offset` is the second point along the node's own bone (21.7). */
const anchor = checkShape({ required: {}, optional: { node: name, factor: checkNumber(), pixelOffset: checkNumber(), offset: anchorOffset } });

/** `DrawableLerp` (21.7): the weights blend between the two nodes' points, so each lies in [0, 1]. */
const lerp = checkShape({
  required: { node: name, w: weight("lerp.w in [0, 1]: 0 = this drawable's own point, 1 = the point on lerp.node (21.7)") },
  optional: { wB: weight('lerp.wB in [0, 1] blends the second point; absent = w (21.7)') },
});

/** Shape per drawable kind; `kind` itself is checked before the lookup. */
export const DRAWABLE_SHAPES: Readonly<Record<Drawable['kind'], Shape>> = {
  impostor: {
    required: { ...common, sheer, zOrder: checkNumber(), shadow: checkBoolean, facets },
    optional: {
      anchor,
      lerp,
      onMissingFrame: checkEnum(['default', 'hide']),
    },
  },
  mesh: { required: { ...common, mesh: checkString(), zOrder: checkNumber() }, optional: { toon: checkBoolean } },
  decal: { required: { ...common, image: checkString(), size: checkTuple(2, { above: 0 }) } },
  effect: {
    required: {
      ...common, pivot: checkTuple(2), sheer, zOrder: checkNumber(), blend: checkEnum(['alpha', 'add']),
      frames: checkShape({
        required: {
          sheet: name, origin: checkTuple(2, { int: true, min: 0 }), size: checkTuple(2, { int: true, min: 1 }),
          count: checkNumber({ int: true, min: 1 }), fps: checkNumber({ above: 0 }), loop: checkBoolean,
        },
      }),
      trigger: checkShape({ required: { start: checkString() }, optional: { stop: checkString() } }),
    },
    optional: { facets },
  },
};
